/**
 * E03-F08-T01 (FP-05) — PERSISTED sourcing-team assignment card.
 *
 * Rendered in the Generation stage (host: ProjectDetailView → GenerationStageBody).
 * Unlike the EPHEMERAL "Send to Sourcing Team" email-recipient picker in
 * SendEmailDialog (which only fills a "To:" field and then discards the team),
 * this card assigns a team that is STORED on `projects.sourcing_team_id` and so
 * survives a page reload.
 *
 * It reads the currently-assigned team from the project detail card
 * (`sourcing_team` / `sourcing_team_id`, refetched after every change) and writes
 * via `useAssignSourcingTeam` → PATCH /projects/:id/sourcing-team. Options come
 * from the shared `useSourcingTeams()` list (active teams only). A team that was
 * soft-deleted after being assigned is still shown as a synthetic "(inactive)"
 * option so the current value never silently disappears from the `<select>`.
 */
import { type JSX } from 'react';
import type { ProjectSourcingTeamRef } from '@eer/shared';
import { useSourcingTeams } from '../admin/sourcingTeamsApi.js';
import { useAssignSourcingTeam } from './projectDetailApi.js';
import { useToast } from '../ui/ToastContext.js';

export interface SourcingTeamCardProps {
  readonly projectId: number;
  /** Currently-stored FK (null = unassigned) — from the project detail card. */
  readonly assignedTeamId: number | null;
  /** Resolved assigned team (null = unassigned) — from the project detail card. */
  readonly assignedTeam: ProjectSourcingTeamRef | null;
}

const SELECT_CLASS =
  'w-full rounded-md border border-[var(--color-border)] p-2 text-sm outline-none focus:border-[var(--color-accent)] disabled:opacity-60';

export function SourcingTeamCard({
  projectId,
  assignedTeamId,
  assignedTeam,
}: SourcingTeamCardProps): JSX.Element {
  const toast = useToast();
  const teamsQuery = useSourcingTeams();
  const assign = useAssignSourcingTeam(projectId);

  const teams = teamsQuery.data ?? [];

  // The active teams as {id,name} options. If the currently-assigned team is not
  // in the active list (soft-deleted after assignment), append it so the select's
  // value still resolves to a visible option (contract note: soft-deleted teams
  // must still render on older projects).
  const options: { id: number; name: string }[] = teams.map((t) => ({ id: t.id, name: t.name }));
  if (assignedTeamId != null && assignedTeam && !teams.some((t) => t.id === assignedTeamId)) {
    options.push({ id: assignedTeam.id, name: `${assignedTeam.name} (inactive)` });
  }

  const currentValue = assignedTeamId != null ? String(assignedTeamId) : '';
  const busy = assign.isPending;

  async function onSelect(raw: string): Promise<void> {
    const nextId = raw === '' ? null : Number(raw);
    if (nextId === assignedTeamId) return; // no-op (re-select same value)
    try {
      await assign.mutateAsync(nextId);
      toast.success(nextId === null ? 'Sourcing team cleared' : 'Sourcing team assigned');
    } catch {
      toast.error('Failed to update the sourcing team');
    }
  }

  return (
    <div
      data-testid="sourcing-team-card"
      className="rounded-lg border border-[var(--color-border)] p-4 bg-white flex flex-col gap-3"
    >
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="text-xl leading-none">
          👥
        </span>
        <div className="flex flex-col">
          <span className="font-medium text-[var(--color-text-strong)]">Sourcing Team</span>
          <span className="text-xs text-[var(--color-text-muted)]">
            Assign the sourcing team responsible for this search (persists on the project)
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <label className="flex flex-col gap-1 text-sm flex-1 min-w-0">
          <span className="sr-only">Sourcing team</span>
          <select
            data-testid="sourcing-team-select"
            className={SELECT_CLASS}
            value={currentValue}
            disabled={busy || teamsQuery.isPending}
            onChange={(e) => void onSelect(e.target.value)}
          >
            <option value="">— Unassigned —</option>
            {options.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        {busy && (
          <span
            data-testid="sourcing-team-saving"
            className="inline-block w-4 h-4 border-2 border-[var(--color-border)] border-t-[var(--color-accent)] rounded-full animate-spin shrink-0"
            aria-hidden="true"
          />
        )}
      </div>

      <div className="text-sm">
        {assignedTeam ? (
          <span
            data-testid="sourcing-team-assigned-label"
            className="text-[var(--color-text-strong)]"
          >
            Assigned sourcing team: <strong>{assignedTeam.name}</strong>
            {assignedTeam.members.length > 0 &&
              ` · ${assignedTeam.members.length} member${assignedTeam.members.length === 1 ? '' : 's'}`}
          </span>
        ) : (
          <span data-testid="sourcing-team-empty" className="text-[var(--color-text-muted)]">
            No sourcing team assigned
          </span>
        )}
      </div>
    </div>
  );
}

export default SourcingTeamCard;
